import { useState } from "react";
import { X, ImageOff } from "lucide-react";
import { GALLERY_CATEGORIES, SITE } from "@/lib/site";

export default function Gallery() {
  const [activeCat, setActiveCat] = useState(GALLERY_CATEGORIES[0].id);
  const [lightbox, setLightbox] = useState(null);

  const current = GALLERY_CATEGORIES.find((c) => c.id === activeCat) || GALLERY_CATEGORIES[0];

  return (
    <section id="galerie" data-testid="gallery-section" className="relative py-24 lg:py-36 px-5 lg:px-10 bg-[#fcf3e6]">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="text-[#048a93] uppercase text-[11px] tracking-[0.32em] font-semibold mb-5">
              <span className="inline-block h-px w-10 bg-[#048a93] align-middle mr-3" />
              Galerie
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-[#012a2d]" data-testid="gallery-title">
              Evenimente trăite<br />
              la <span className="italic text-[#048a93]">AriDanya</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-8">
            <p className="font-sans-ui text-[#1a2421]/75 leading-relaxed">
              Selectează tipul de eveniment pentru a vedea momente surprinse în locația noastră.
            </p>
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-3 mb-10" data-testid="gallery-tabs">
          {GALLERY_CATEGORIES.map((cat) => {
            const isActive = cat.id === activeCat;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCat(cat.id)}
                data-testid={`gallery-tab-${cat.id}`}
                className={`group inline-flex items-center gap-3 rounded-full px-6 py-3 transition-all font-sans-ui text-sm border ${
                  isActive
                    ? "bg-[#012a2d] text-[#fcf3e6] border-[#012a2d]"
                    : "bg-transparent text-[#012a2d] border-[#012a2d]/20 hover:border-[#048a93] hover:text-[#048a93]"
                }`}
              >
                <span className="font-serif-display text-base">{cat.label}</span>
              </button>
            );
          })}
        </div>

        {/* Category description */}
        <p className="font-serif-display italic text-xl lg:text-2xl text-[#012a2d]/75 mb-10 max-w-2xl" data-testid="gallery-category-description">
          {current.description}
        </p>

        {/* Photos grid */}
        {current.photos.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[160px] md:auto-rows-[220px] gap-4" data-testid={`gallery-grid-${current.id}`}>
            {current.photos.map((p, idx) => (
              <button
                key={idx}
                onClick={() => setLightbox(p)}
                data-testid={`gallery-item-${idx}`}
                className={`group relative overflow-hidden rounded-3xl bg-[#012a2d] ${
                  idx === 0 ? "row-span-2 col-span-2" : "col-span-2 md:col-span-1"
                }`}
              >
                <img
                  src={p.url}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#012a2d]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        ) : (
          <div
            className="border border-dashed border-[#012a2d]/20 rounded-3xl py-20 px-6 text-center bg-[#fcf3e6]"
            data-testid={`gallery-empty-${current.id}`}
          >
            <div className="h-14 w-14 rounded-2xl bg-[#048a93]/10 text-[#048a93] flex items-center justify-center mx-auto mb-5">
              <ImageOff className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif-display text-2xl lg:text-3xl text-[#012a2d]">În curând</h3>
            <p className="font-sans-ui text-sm text-[#1a2421]/65 mt-3 max-w-md mx-auto">
              Adăugăm în curând fotografii din această categorie. Între timp, ne poți contacta pentru detalii.
            </p>
            <a
              href={SITE.phoneHref}
              className="inline-flex items-center gap-2 mt-6 text-[#048a93] hover:text-[#012a2d] font-sans-ui text-sm font-medium transition"
            >
              Sună acum · {SITE.phoneDisplay} →
            </a>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-[#012a2d]/95 backdrop-blur-md flex items-center justify-center p-4 lg:p-10"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-6 right-6 h-12 w-12 rounded-full bg-[#fcf3e6]/10 hover:bg-[#048a93] text-[#fcf3e6] flex items-center justify-center transition"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox-close"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightbox.url}
            alt=""
            className="max-h-[85vh] max-w-full object-contain rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
